import { useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import { SpaceLocalAttributeValueSetDocument } from "#graphql/supergraphHooks";
import {
  type SpaceLocalAttributeValueSetQuery,
  type SpaceLocalAttributeValueSetQueryVariables,
} from "#graphql/supergraphTypes";

/**
 * The pre-save value-set read failed, so the save was abandoned before the
 * mutation was attempted.
 *
 * Distinct from a server refusal of the edit: falling through to the editor's
 * stale snapshot would re-open the concurrent-delete bug the read exists to
 * prevent, so failing closed is correct — but the caller must not then tell the
 * user their change was rejected, because nothing was ever sent.
 */
export class SpaceLocalValueSetReadError extends Error {
  // Set explicitly: `constructor.name` is not survivable through minification,
  // and `instanceof` breaks across duplicate module instances.
  readonly name = "SpaceLocalValueSetReadError";

  constructor(readonly cause: unknown) {
    super("could not read the attribute's current value set");
  }
}

export const isValueSetReadError = (error: unknown): error is SpaceLocalValueSetReadError =>
  error instanceof Error && error.name === "SpaceLocalValueSetReadError";

/** One space-local attribute's value set as it stands on the server right now. */
export interface LiveValueSet {
  /** Option values in `displayOrder`. */
  options: string[];
  /** Display value -> backend option id, for an id-stable patch. */
  optionIdsByValue: Record<string, string>;
}

export interface UseSpaceLocalValueSetReturn {
  /**
   * Read one space-local attribute's current value set, or `null` when the
   * attribute is not among the rows the query returns (deleted, moved, or no
   * longer visible to this caller).
   */
  readValueSet: (
    spaceId: string,
    attributeId: string,
    slug: string,
  ) => Promise<LiveValueSet | null>;
}

/**
 * Imperative pre-save read of a space-local attribute's value set (DV-674).
 *
 * `updateSpaceLocalAttribute`'s `options` is the desired FULL ordered set and an
 * omitted entry is a **removal**, so the set has to be rebuilt against what the
 * server holds at save time. Diffing against the snapshot the editor opened with
 * would silently delete any option another admin added while the modal sat open.
 *
 * `no-cache`: the point is to see another admin's write, which a cache hit from
 * this session would hide.
 */
export const useSpaceLocalValueSet = (): UseSpaceLocalValueSetReturn => {
  const client = useApolloClient();

  const readValueSet = useCallback(
    async (spaceId: string, attributeId: string, slug: string) => {
      const { data } = await client.query<
        SpaceLocalAttributeValueSetQuery,
        SpaceLocalAttributeValueSetQueryVariables
      >({
        query: SpaceLocalAttributeValueSetDocument,
        variables: {
          input: {
            spaceIds: [spaceId],
            nameFilter: { operator: "equals", value: slug },
            // Not 1: the filter matches label OR slug, so another attribute
            // whose label equals this slug can sort ahead of the one we want.
            first: 10,
          },
        },
        fetchPolicy: "no-cache",
      });

      // The filter matches label OR slug, so a row can come back for a
      // different attribute whose label happens to equal this slug.
      const node = data?.spaceLocalAttributesSearch?.nodes.find(
        (n) => n.attribute.id === attributeId,
      );
      if (!node) return null;

      const ordered = [...node.attribute.options].sort(
        (a, b) => a.displayOrder - b.displayOrder,
      );
      return {
        options: ordered.map((o) => o.value),
        optionIdsByValue: Object.fromEntries(ordered.map((o) => [o.value, o.id])),
      };
    },
    [client],
  );

  return { readValueSet };
};
